import type { FrameThumbnailTheme } from "@/api/frame-listing";
import { useThemeChoice } from "@/hooks/use-theme-choice";

/**
 * The poster theme cards show in the current app theme: paper cards (light)
 * the light poster, ink cards (dark) the dark one.
 */
export function usePosterTheme(): FrameThumbnailTheme {
  return useThemeChoice();
}

function storedPoster(
  thumbnail: unknown,
  theme: FrameThumbnailTheme,
): Record<string, unknown> | null {
  if (!thumbnail || typeof thumbnail !== "object") return null;
  const poster = (thumbnail as Record<string, unknown>)[theme];
  return poster && typeof poster === "object"
    ? (poster as Record<string, unknown>)
    : null;
}

/** The stored poster URL for `theme` in `metadata.thumbnail`, else `""`. */
export function posterUrl(
  thumbnail: unknown,
  theme: FrameThumbnailTheme,
): string {
  const url = storedPoster(thumbnail, theme)?.url;
  return typeof url === "string" ? url : "";
}

/** The width / height the server recorded for `theme`'s poster, else null. */
export function posterAspect(
  thumbnail: unknown,
  theme: FrameThumbnailTheme,
): number | null {
  const poster = storedPoster(thumbnail, theme);
  const width = poster?.width;
  const height = poster?.height;
  return typeof width === "number" &&
    typeof height === "number" &&
    width > 0 &&
    height > 0
    ? width / height
    : null;
}

/** What an HTML page's card shows, measured with its poster. */
export interface HtmlCover {
  /** The poster is the card, for every page. */
  kind: "cover";
  headline: string;
  line: string;
  /** The page's own colour as `#rrggbb`. */
  tint: string | null;
  /** The poster's average colour, shown while it loads. */
  placeholder: string | null;
}

const HEX_COLOR = /^#[0-9a-f]{6}$/;

function storedColor(value: unknown): string | null {
  return typeof value === "string" && HEX_COLOR.test(value) ? value : null;
}

/**
 * The cover stored beside `theme`'s poster, else null (an older poster). A
 * stored `block` (a text-only page, captured before every page showed its
 * poster) is read as a cover.
 */
export function posterCover(
  thumbnail: unknown,
  theme: FrameThumbnailTheme,
): HtmlCover | null {
  const cover = storedPoster(thumbnail, theme)?.cover;
  if (!cover || typeof cover !== "object") return null;
  const record = cover as Record<string, unknown>;
  if (record.kind !== "cover" && record.kind !== "block") return null;
  const text = (key: string) =>
    typeof record[key] === "string" ? (record[key] as string) : "";
  return {
    kind: "cover",
    headline: text("headline"),
    line: text("line"),
    tint: storedColor(record.tint),
    placeholder: storedColor(record.placeholder),
  };
}

/**
 * Whether `theme`'s poster is stored with its cover. A poster from before
 * covers were measured is taken again once.
 */
export function hasThemePoster(
  thumbnail: unknown,
  theme: FrameThumbnailTheme,
): boolean {
  return (
    posterUrl(thumbnail, theme) !== "" && posterCover(thumbnail, theme) !== null
  );
}
